import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRecoilState, useRecoilValue } from "recoil";

import { taskPopupState } from "../../../recoil/taskDetail/taskPopupState";

import {
  getVisibilityComponent,
  getVisibilityTooltipContents,
  getVisibilityDropdownComponent,
} from "./getVisibilityComponents";

import clsx from "clsx";
import styles from "./Header.module.css";
import { accountState } from "../../../recoil/account/accountStateV2";
import { PriorityButton } from "./PriorityButton";
import { PriorityButtonPopover } from "./PriorityButtonPopover";

import { RSVPMenu } from "../RSVP/RSVPMenu";
import { BasicBlock } from "../../../types/block/enum";
import { getBlockTypeFlags } from "../../../utils/common/block/getBlockTypeFlags";
import MoreIcon from "../../../compoenets/atom/icons/MoreIcon";
import FullScreenIcon from "../../../compoenets/atom/icons/FullScreenIcon";
import ExitFullScreenIcon from "../../../compoenets/atom/icons/ExitFullScreenIcon";
import BackIcon from "../../../compoenets/atom/icons/BackIcon";
import { useIsMobileDevice } from "../../../hooks/mobile/useIsMobileDevice";

function NoteModalHeader({ setIsModalNoteClicked }) {
  const taskDetail = useRecoilValue(taskPopupState);
  return (
    <div
      className={styles["note-modal-header"]}
      style={{ display: "flex", color: "white" }}
    >
      <div
        className={styles.backIcon}
        onClick={(e) => setIsModalNoteClicked(false)}
      >
        <BackIcon color={"#CACDCE"} />
      </div>
      <div className={styles["note-modal-header__title"]}>
        <span>
          {taskDetail.data.title ? taskDetail.data.title : `New ${taskDetail.data.blockType}`}
        </span>
      </div>
    </div>
  );
}

function Header({
  moreModalRef,
  visibilityDropdownRef,
  toggleExpand,
  setToggleExpand,
  isDisabled,
  isVisibilityClick,
  isSidebarModalOn,
  selectedVisibilityType,
  isModalNoteClicked,
  currentRSVPValue,
  setIsModalNoteClicked,
  onChangePriority,
  handleVisibilityClick,
  handleVisibilityDropdownItemClick,
  handleTaskDelete,
  handleChangeRSVP,
  handleMoreButtonClick,
}) {
  const [isPriorityPopoverOpen, setIsPriorityPopoverOpen] = useState(false);
  const [taskDetail, setTaskDetail] = useRecoilState(taskPopupState);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const accountData = useRecoilValue(accountState);

  const { isTask, isEvent } = getBlockTypeFlags(taskDetail.data.blockType);

  const toggleRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (tooltipRef.current && toggleRef.current) {
      const toggleRect = toggleRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      setTooltipPosition({
        x: toggleRect.left,
        y: toggleRect.top - tooltipRect.height - 5,
      });
    }
  }, [showTooltip]);

  const dropdownList = Object.entries({
    public: "Public",
    deepwork: "Deep work",
    invisible: "Invisible",
  });

  const onDataDuplicate = (e) => {
    taskDetail.handleDataDuplicate(e, taskDetail.data);
  };

  const handleShowTooltip = () => {
    if (taskDetail.data?.attendees?.length > 0) {
      setShowTooltip(true);
    }
  };

  const handleHiddenTooltip = () => {
    if (taskDetail.data?.attendees?.length > 0) {
      setShowTooltip(false);
    }
  };

  const isMobileDevice = useIsMobileDevice();

  return (
    <div
      className={clsx(styles.header, {
        [styles["header-mini"]]: !toggleExpand,
        [styles["header-expand"]]: toggleExpand,
      })}
    >
      {isModalNoteClicked ? (
        <NoteModalHeader setIsModalNoteClicked={setIsModalNoteClicked} />
      ) : taskDetail.type !== "inbox" ? (
        <div
          className={styles.toggle_container}
          ref={toggleRef}
        >
          {showTooltip &&
            createPortal(
              <div
                ref={tooltipRef}
                className={styles.blockType_tooltip}
                style={{
                  left: `${tooltipPosition.x}px`,
                  top: `${tooltipPosition.y}px`,
                  position: "absolute",
                }}
              >
                <span>Event with guests cannot be converted to task</span>
              </div>,
              document.body
            )}
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={!isTask}
              disabled={
                taskDetail.type == "inbox" ||
                // NOTE taskType이 event이고 attendees가 있을 때 disabled
                taskDetail?.data?.attendees?.length > 0
              }
              onChange={
                taskDetail.type !== "inbox"
                  ? (e) => {
                      setTaskDetail((prev) => ({
                        ...prev,
                        data: {
                          ...prev.data,
                          blockType:
                            prev.data.blockType === BasicBlock.TASK
                              ? BasicBlock.EVENT
                              : BasicBlock.TASK,
                        },
                      }));
                    }
                  : undefined
              }
            />
            <span className={styles.slider}>
              <div>
                <div
                  className={styles.slider_text_box}
                  onMouseEnter={handleShowTooltip}
                  onMouseLeave={handleHiddenTooltip}
                >
                  <span
                    className={clsx(styles.slider_text_Task, {
                      [styles.selected_text]: isTask,
                    })}
                  >
                    Task
                  </span>
                </div>
                <div className={styles.slider_text_box}>
                  <span
                    className={clsx(styles.slider_text_Event, {
                      [styles.selected_text]: isEvent,
                    })}
                  >
                    Event
                  </span>
                </div>
              </div>
            </span>
          </label>
        </div>
      ) : (
        <div></div>
      )}

      <div className={styles["header-menu"]}>
        {!isModalNoteClicked && (
          <>
            {toggleExpand && isEvent && taskDetail.data?.attendees?.length > 0 && (
              <RSVPMenu
                currentValue={currentRSVPValue}
                onChangeValue={handleChangeRSVP}
              />
            )}
            <div
              className={styles.visibilityArea}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {getVisibilityComponent(
                selectedVisibilityType,
                isVisibilityClick,
                handleVisibilityClick
              )}
              {isVisibilityClick && (
                <div
                  className={styles.visibilityDropdownBox}
                  ref={visibilityDropdownRef}
                >
                  {dropdownList.map((label) => (
                    <div
                      key={label[0]}
                      className={`${styles.dropdownListItem} ${
                        selectedVisibilityType === label[0] ? styles.active : ""
                      }`} // 수정된 조건부 클래스 추가 방식
                      onClick={() => handleVisibilityDropdownItemClick(label[0])}
                    >
                      {getVisibilityDropdownComponent(
                        label[0],
                        selectedVisibilityType === label[0]
                      )}
                      <div className={styles.dropdownTitle}>{label[1]}</div>
                      <div className={styles.dropdownInfoIcon}>
                        <div className={styles.dropdownInfoDescription}>
                          <span className={styles.infoDescriptionText}>
                            {getVisibilityTooltipContents(label[0])}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {isTask && (
              <PriorityButtonPopover
                menuVisible={isPriorityPopoverOpen}
                handleMenuClose={() => setIsPriorityPopoverOpen(false)}
                currentPriorityValue={taskDetail.data.priority}
                onChange={(priority) => onChangePriority(priority)}
              >
                <PriorityButton
                  currentValue={taskDetail.data.priority}
                  onClick={() => setIsPriorityPopoverOpen(true)}
                  isMenuVisible={isPriorityPopoverOpen}
                />
              </PriorityButtonPopover>
            )}

            <div className={styles.headerMenuAccArea}>
              <div
                className={styles["header-menu-acc"]}
                style={{
                  backgroundImage: `url(${accountData.photo})`,
                  width: "24px",
                  height: "24px",
                  backgroundSize: "cover",
                }}
                // onClick={() => setIsAccModal(!isAccModal)}
              ></div>
            </div>
          </>
        )}

        {!isModalNoteClicked && !isDisabled && (
          <div
            className={styles["header__more--btn"]}
            onClick={handleMoreButtonClick}
          >
            <>
              <MoreIcon color={"#CACDCE"} />
              {isSidebarModalOn && (
                <div
                  className={styles["modal"]}
                  ref={moreModalRef}
                >
                  <button onClick={(e) => onDataDuplicate(e)}>Duplicate</button>
                  <button onClick={async () => await handleTaskDelete()}>Delete</button>
                </div>
              )}
            </>
          </div>
        )}

        {!isMobileDevice &&
          (!toggleExpand ? (
            <button
              className={styles.headerMenuExpand}
              onClick={() => {
                if (!toggleExpand) {
                  setIsModalNoteClicked(false);
                }
                setToggleExpand(!toggleExpand);
              }}
            >
              <FullScreenIcon color={"#CACDCE"} />
            </button>
          ) : (
            <button
              className={styles.header__shrink}
              onClick={() => setToggleExpand(!toggleExpand)}
            >
              <ExitFullScreenIcon color={"#CACDCE"} />
            </button>
          ))}
      </div>
    </div>
  );
}

export default Header;
